import { useState } from "react";
import { toast } from "react-toastify";
import PageHeader from "../../Component/PageHeader";
import StatStrip from "../../Component/StatStrip";
import PeriodFilter from "../../Component/PeriodFilter";
import Pill from "../../Component/Pill";
import TenantDrawer from "./components/TenantDrawer";
import AddPartnerModal from "./components/AddPartnerModal";
import { DemoTenant, TENANTS } from "../../utils/staticData";

// "Add reseller" uses the real API (AddPartnerModal → /white-label/create-partner).
// The reseller cards + branding drawer are STATIC DESIGN until a tenant list/config API exists.
// (previous version of this page: pages/Old/WhiteLabel)
const lakh = (n: number) => (n ? "₹" + (n / 100000).toFixed(1) + "L" : "—");

const WhiteLabel = () => {
  const [tenants, setTenants] = useState<DemoTenant[]>(TENANTS);
  const [period, setPeriod] = useState("week");
  const [editing, setEditing] = useState<number | null>(null); // index of the reseller being managed
  const [showAddModal, setShowAddModal] = useState(false);
  const drawerOpen = editing !== null;

  const active = tenants.filter(t => t.status === "active").length;
  const resellerClients = tenants.reduce((s, t) => s + t.clients, 0);
  const resellerMrr = tenants.reduce((s, t) => s + t.mrr, 0);

  const save = (t: DemoTenant) => {
    if (editing !== null) setTenants(list => list.map((x, i) => (i === editing ? t : x)));
    setEditing(null);
    toast.success("Branding saved · cache cleared");
  };

  return (
    <div className="container-fluid">
      <div className="cx">
        <PageHeader
          crumb="White-label"
          title="White-label resellers"
          sub="Each reseller has its own domain, logo and colours — loaded into the client app from the tenantConfig API."
          actions={<>
            <PeriodFilter value={period} onChange={setPeriod} />
            <button type="button" className="cx-btn cx-btn-primary" onClick={() => setShowAddModal(true)}><i className="ri-add-line"></i>Add reseller</button>
          </>}
        />

        <StatStrip items={[
          { label: "Total Resellers", value: tenants.length, icon: "ri-store-2-line", delta: 33.3 },
          { label: "Active Resellers", value: active, icon: "ri-checkbox-circle-line", delta: 0 },
          { label: "Pending Setup", value: tenants.length - active, icon: "ri-time-line", delta: 100, invert: true },
          { label: "Reseller Clients", value: resellerClients, icon: "ri-group-line", delta: 7.6 },
          { label: "Reseller MRR", value: lakh(resellerMrr), icon: "ri-money-rupee-circle-line", delta: 9.4 },
        ]} />

        <div className="cx-grid g2">
          {tenants.map((t, i) => (
            <div className="cx-card" key={`${t.name}-${i}`}>
              <div className="cx-card-b" style={{ padding: 16 }}>
                <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: t.color, color: "#fff", display: "grid", placeItems: "center", fontFamily: "var(--display)", fontWeight: 700, fontSize: 18, flex: "none" }}>
                    {t.name[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <b style={{ fontSize: 15 }}>{t.name}</b>
                      <Pill tone={t.status === "active" ? "ok" : "warn"}>{t.status}</Pill>
                    </div>
                    <div className="mono fs12 muted">{t.domain}</div>
                    <div className="fs12 muted">{t.owner || "—"}</div>
                  </div>
                  <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => setEditing(i)}><i className="ri-settings-3-line"></i>Manage</button>
                </div>
                <div className="cx-kv k3" style={{ marginTop: 14 }}>
                  <div><small>Clients</small><b className="mono">{t.clients}</b></div>
                  <div><small>Reseller MRR</small><b className="mono">{lakh(t.mrr)}</b></div>
                  <div><small>Wholesale margin</small><b className="mono">{t.margin}%</b></div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <Pill tone={t.dns === "ok" ? "ok" : "bad"}>DNS {t.dns === "ok" ? "verified" : "missing"}</Pill>
                  <Pill tone={t.ssl === "ok" ? "ok" : "warn"}>SSL {t.ssl === "ok" ? "active" : "pending"}</Pill>
                  <Pill tone="none">Meta App: shared</Pill>
                </div>
              </div>
            </div>
          ))}
        </div>

        <TenantDrawer
          show={drawerOpen}
          tenant={editing !== null ? tenants[editing] : null}
          onClose={() => setEditing(null)}
          onSave={save}
        />
        <AddPartnerModal show={showAddModal} handleClose={() => setShowAddModal(false)} />
      </div>
    </div>
  );
};

export default WhiteLabel;
